import { useTranslation } from "react-i18next"
import { Tag } from "@/components/ui/tag"
import {
  BetterDocTable,
  BetterDocTableCell,
  BetterDocTableHeaderCell,
} from "@/modules/admin/better-doc/components/better-doc-table"
import { EMPTY_VALUE_PLACEHOLDER } from "@/modules/admin/better-doc/constants/better-doc-constants"
import { getBetterDocEnumLabel } from "@/modules/admin/better-doc/lib/better-doc-enum-label"
import { formatBetterDocBoolean, formatBetterDocValue } from "@/modules/admin/better-doc/lib/format-better-doc-value"
import { BetterDocAnamnesisInfoSchema } from "@/modules/admin/better-doc/services/betterDocAnamnesisInfoSchemasQuery"

type BetterDocAnamnesisInfoSchemaTableProps = {
  schema: BetterDocAnamnesisInfoSchema
}

export const BetterDocAnamnesisInfoSchemaTable = ({ schema }: BetterDocAnamnesisInfoSchemaTableProps) => {
  const { t } = useTranslation()
  const selectorText =
    schema.selector.length === 0
      ? t("admin.betterDoc.anamnesisInfoSchemas.anyRequest")
      : schema.selector.map((selectorEntry) => `${selectorEntry.path} = ${selectorEntry.value}`).join(", ")

  return (
    <section className="flex flex-col gap-2" data-testid="better-doc-admin-anamnesis-info-schema">
      <div className="flex flex-wrap items-center gap-2">
        <h3
          className="text-on-surface text-base font-medium"
          data-testid="better-doc-admin-anamnesis-info-schema-title">
          {t("admin.betterDoc.anamnesisInfoSchemas.title", { name: schema.name, version: schema.version })}
        </h3>
        <Tag
          hasIcon={false}
          label={getBetterDocEnumLabel(t, "anamnesisInfoSchemaStatus", schema.status)}
          data-testid="better-doc-admin-anamnesis-info-schema-status"
        />
      </div>
      <p className="text-on-surface-subdued text-sm" data-testid="better-doc-admin-anamnesis-info-schema-selector">
        {t("admin.betterDoc.anamnesisInfoSchemas.selector", { selector: selectorText })}
      </p>
      <BetterDocTable data-testid="better-doc-admin-anamnesis-info-schema-table">
        <thead>
          <tr>
            <BetterDocTableHeaderCell>{t("admin.betterDoc.anamnesisInfoSchemas.attribute")}</BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>{t("admin.betterDoc.anamnesisInfoSchemas.valueType")}</BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>{t("admin.betterDoc.anamnesisInfoSchemas.requirement")}</BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>
              {t("admin.betterDoc.anamnesisInfoSchemas.allowedValues")}
            </BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>{t("admin.betterDoc.anamnesisInfoSchemas.description")}</BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>{t("admin.betterDoc.anamnesisInfoSchemas.action")}</BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>{t("admin.betterDoc.anamnesisInfoSchemas.source")}</BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>{t("admin.betterDoc.anamnesisInfoSchemas.isNullable")}</BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>
              {t("admin.betterDoc.anamnesisInfoSchemas.itemDefinition")}
            </BetterDocTableHeaderCell>
          </tr>
        </thead>
        <tbody>
          {schema.attributes.map((attribute) => (
            <tr
              key={attribute.name}
              className="hover:bg-surface-hovered"
              data-testid="better-doc-admin-anamnesis-info-schema-attribute-row">
              <BetterDocTableCell className="font-mono text-xs">{attribute.name}</BetterDocTableCell>
              <BetterDocTableCell>
                {attribute.valueType === null
                  ? EMPTY_VALUE_PLACEHOLDER
                  : getBetterDocEnumLabel(t, "anamnesisValueType", attribute.valueType)}
              </BetterDocTableCell>
              <BetterDocTableCell className="font-mono text-xs break-all">
                {formatBetterDocValue(attribute.requirementJson)}
              </BetterDocTableCell>
              <BetterDocTableCell className="font-mono text-xs">
                {attribute.allowedValuesJson.length === 0
                  ? EMPTY_VALUE_PLACEHOLDER
                  : attribute.allowedValuesJson.join(", ")}
              </BetterDocTableCell>
              <BetterDocTableCell>{formatBetterDocValue(attribute.description)}</BetterDocTableCell>
              <BetterDocTableCell>
                {attribute.action === null
                  ? EMPTY_VALUE_PLACEHOLDER
                  : getBetterDocEnumLabel(t, "anamnesisAttributeAction", attribute.action)}
              </BetterDocTableCell>
              <BetterDocTableCell className="font-mono text-xs">
                {formatBetterDocValue(attribute.source)}
              </BetterDocTableCell>
              <BetterDocTableCell>{formatBetterDocBoolean(t, attribute.isNullable)}</BetterDocTableCell>
              <BetterDocTableCell className="font-mono text-xs break-all">
                {formatBetterDocValue(attribute.itemDefinitionJson)}
              </BetterDocTableCell>
            </tr>
          ))}
        </tbody>
      </BetterDocTable>
    </section>
  )
}
